import { ChangeDetectionStrategy, Component, computed, inject, signal, TemplateRef, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LoaderComponent } from '@app/core/components/loader/loader.component';
import {
  isValidPostLoginRedirectUrl,
  POST_LOGIN_REDIRECT_URL_KEY,
} from '@app/core/config/auth-redirect.config';
import { LoginResult } from '@app/core/models/login-result.model';
import { AuthApiService } from '@app/core/services/auth-api.service';
import { ClientStorageService } from '@app/core/services/client-storage.service';
import { UserContext } from '@app/shared/models/access-control/user-context.model';
import { AxPortalResponse } from '@app/shared/models/responses.model';
import { accessLevel } from '@app/shared/models/role-enum';
import { UserContextService } from '@app/shared/services/access-control/user-context.service';
import { ConstantsService } from '@app/shared/services/internal-services/constants-internal.service';
import { ResetPasswordApiService } from '@app/shared/services/reset-password-api.service';
import { AuthContextSelectors } from '@app/shared/state/auth-context/auth-context.selectors';
import { DomainContextSelectors } from '@app/shared/state/domain-context/domain-context.selectors';
import { SetUserContext } from '@app/shared/state/user-context/user-context.action';
import { UserContextSelectors } from '@app/shared/state/user-context/user-context.selectors';
import { isNullOrEmpty } from '@app/shared/utils/string.util';
import { Store } from '@ngxs/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputText } from 'primeng/inputtext';
import { catchError, EMPTY, finalize, map, switchMap } from 'rxjs';
import { ContactSupportComponent } from '../modals/contact-support/contact-support.component';

@Component({
    selector: 'login',
    imports: [ContactSupportComponent, FormsModule, FloatLabelModule, InputText, LoaderComponent],
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent {

    private readonly userContextService = inject(UserContextService)
    private readonly store = inject(Store)
    private readonly modalService = inject(BsModalService)
    private readonly authService = inject(AuthApiService)
    private readonly resetPasswordService = inject(ResetPasswordApiService)
    private readonly router = inject(Router)
    private readonly clientStorageService = inject(ClientStorageService)

    @ViewChild('contactSupportTemplate', { static: false }) contactSupportTemplate!: TemplateRef<void>;
    readonly contactSupportModalId: string = 'contactSupportModalId'

    username = signal<string>('')
    password = signal<string>('')
    errorMessage = signal<string | undefined>(undefined)
    passwordReset = signal<boolean>(false)
    infoMessagePasswordReset = signal<string | undefined>(undefined)
    errorMessagePasswordReset = signal<string | undefined>(undefined)
    domain = this.store.selectSignal(DomainContextSelectors.companyId)
    isProcessingLogin = signal<boolean>(false)
    logoutMessage =  toSignal(
      this.clientStorageService.watch<{message: string}>('logoutMessage').pipe(map(m => m?.message ?? null)), { initialValue: null}
    )

    inactiveMessageArr = computed(() => {
      const msg = this.logoutMessage()

      if(msg?.includes('$')) {
        return msg.split('$')
      }

      return msg ? [msg] : null
    })

    clearLogoutMessage() {
      this.clientStorageService.removeItem('logoutMessage')
    }

    openModalContactSupport(): void {
        this.modalService.show(this.contactSupportTemplate, { id: this.contactSupportModalId, class: 'modal-md' })
    }

    onKeyEnter(): void {
        if (!this.credentialsArePresent()) return
        this.login()
    }

    togglePasswordReset(): void {
        this.passwordReset.set(!this.passwordReset());
        this.errorMessage.set(undefined);
        this.errorMessagePasswordReset.set(undefined);
        this.infoMessagePasswordReset.set(undefined);
    }

    /** Password reset is completly anonymous. Sends an email to the user with a link to the password reset page */
    doPasswordReset(): void {
        this.errorMessage.set(undefined);

        this.resetPasswordService.resetPassword(this.username())
          .subscribe({
            next: (response: AxPortalResponse) => {
              this.errorMessagePasswordReset.set(undefined);
              this.infoMessagePasswordReset.set(response.AllOk ? response.Message : ConstantsService.ErrorMessages.PasswordResetGeneric)
            },
            error: (err) => {
              this.infoMessagePasswordReset.set(undefined)
              this.errorMessagePasswordReset.set(!isNullOrEmpty(err?.Message) ? err.Message : ConstantsService.ErrorMessages.PasswordResetGeneric)
            }
          })
    }

    login(): void {
        this.clientStorageService.removeItem('logoutMessage')
        if (this.isProcessingLogin()) return

        this.isProcessingLogin.set(true)
        this.errorMessage.set(undefined)

        if (!this.credentialsArePresent()) {
            this.errorMessage.set('Du måste ange användarnamn och lösenord')
            this.isProcessingLogin.set(false)
            return
        }

        this.authService.login({ username: this.username(), password: this.password() }).pipe(
            switchMap((result: LoginResult) => {
                if (!result.success) {
                    throw new Error(result.errorMessage ?? 'Okänt fel vid inloggning')
                }

                const authData = this.store.selectSnapshot(AuthContextSelectors.authContext)
                if (!authData) {
                    throw new Error("Failed to get AuthData. User context can't be set!");
                }

                return this.userContextService.initLight(authData)
            }),
            switchMap((userCtx: UserContext) => this.store.dispatch(new SetUserContext(userCtx))
            ),
            finalize(() => this.isProcessingLogin.set(false)),
            catchError(err => {
                console.error("Failed to login", err)
                this.loginFailed((err as Error).message)
                return EMPTY
            })
        ).subscribe({
            next: () => this.loginSuccess()
        })
    }

    private credentialsArePresent(): boolean {
        return !isNullOrEmpty(this.username()) && !isNullOrEmpty(this.password())
    }

    private loginSuccess(): void {

      const isAuthenticated = this.store.selectSnapshot(UserContextSelectors.isAuthenticated)

        if (!isAuthenticated) {
            this.errorMessage.set('Felaktigt användarnamn och/eller lösenord');
            return
        }

        this.username.set('');
        this.password.set('');
        const redirectUrl = this.clientStorageService.getItem<string>(POST_LOGIN_REDIRECT_URL_KEY);
        this.clientStorageService.removeItem(POST_LOGIN_REDIRECT_URL_KEY);

        if (isValidPostLoginRedirectUrl(redirectUrl)) {
            this.router.navigateByUrl(redirectUrl);
            return;
        }

        //Redirect to aftermarket if aftermarket user
        if (this.accessLevelAftermarket()) {
            this.router.navigateByUrl('/aftermarketIssues')
        } else {
            this.router.navigateByUrl('/start')
        }
    }

    private accessLevelAftermarket(): boolean {
        const accessPermission = this.store.selectSnapshot(UserContextSelectors.hasPermission('Aftermarket', 'aftermarket_role'))
        const userRole = this.store.selectSnapshot(UserContextSelectors.userRole)
        return ['MANAGER', 'TECHNICIAN', 'ADMIN'].includes(accessPermission.value) || userRole === accessLevel.AFTERMARKET_TECHNICIAN
    }

    private loginFailed(errorMessage?: string): void {
        const errorMessages = new Map<string, string>([
            ['user credentials missing', 'Serverfel. Användarnamn och/eller lösenord kunde inte hittas av servern'],
            ['could not login', 'Kunde inte logga in. Felaktigt användarnamn och/eller lösenord eller spärrat konto'],
            ['session expired', 'Sessionen är inte giltig']
        ])

        this.errorMessage.set(errorMessages.get(errorMessage) || errorMessage || 'Ett okänt fel inträffade vid inloggning')
    }
}
